import React, { useState, useEffect, useRef } from 'react';
import { Send, Lock, AlertCircle, Smile } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MAX_CHARACTERS = 300;

// Professional emojis only
const PROFESSIONAL_EMOJIS = [
  { emoji: '👍', label: 'Thumbs up' },
  { emoji: '👎', label: 'Thumbs down' },
  { emoji: '✅', label: 'Checkmark' },
  { emoji: '❌', label: 'Cross mark' },
  { emoji: '💡', label: 'Lightbulb' },
  { emoji: '⚠️', label: 'Warning' },
  { emoji: '📝', label: 'Memo' },
  { emoji: '📌', label: 'Pin' },
  { emoji: '🔍', label: 'Magnifying glass' },
  { emoji: '💼', label: 'Briefcase' },
  { emoji: '🎯', label: 'Target' },
  { emoji: '⭐', label: 'Star' },
  { emoji: '📊', label: 'Chart' },
  { emoji: '🔔', label: 'Bell' },
  { emoji: '💬', label: 'Speech bubble' },
  { emoji: '📋', label: 'Clipboard' },
];

/**
 * CommentInput - Input field for adding new comments with character limit
 * @param {Function} onSubmit - Callback when comment is submitted (comment, isInternal)
 * @param {boolean} isSubmitting - Whether comment is being submitted
 * @param {string} error - Error message to display
 */
const CommentInput = ({ onSubmit, isSubmitting = false, error = null }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);

  const charactersRemaining = MAX_CHARACTERS - comment.length;
  const isNearLimit = charactersRemaining < 50;
  const isAtLimit = charactersRemaining <= 0;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [comment]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('button[data-emoji-button]')
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const handleChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setComment(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (comment.trim() && !isAtLimit && !isSubmitting) {
      onSubmit(comment.trim(), isInternal);
      setComment('');
      setIsInternal(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e) => {
    // Submit on Enter (but allow Shift+Enter for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleEmojiClick = (emoji) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newComment = comment.substring(0, start) + emoji + comment.substring(end);
      
      // Check character limit
      if (newComment.length <= MAX_CHARACTERS) {
        setComment(newComment);
        // Set cursor position after the inserted emoji
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        }, 0);
      }
    }
    setShowEmojiPicker(false);
  };

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      {/* Error Message */}
      {error && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-in fade-in slide-in-from-top-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Textarea */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={t('comments.placeholder')}
            disabled={isSubmitting}
            rows={1}
            className={`
              w-full px-4 py-3 pr-14 rounded-xl border-2 transition-all duration-200
              resize-none focus:outline-none focus:ring-2
              ${
                isAtLimit
                  ? 'border-red-300 bg-red-50 focus:ring-red-200'
                  : isNearLimit
                  ? 'border-amber-300 bg-amber-50/50 focus:ring-amber-200'
                  : 'border-gray-200 bg-gray-50 focus:border-blue-400 focus:ring-blue-200'
              }
              disabled:opacity-60 disabled:cursor-not-allowed
              placeholder:text-gray-400
            `}
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />

          {/* Emoji Button */}
          <button
            type="button"
            data-emoji-button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isSubmitting || isAtLimit}
            className={`
              absolute right-2 top-1/2 -translate-y-1/2
              w-8 h-8 flex items-center justify-center
              rounded-lg border transition-all duration-200
              ${
                isSubmitting || isAtLimit
                  ? 'border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed'
                  : showEmojiPicker
                  ? 'border-blue-400 bg-blue-50 text-blue-600 shadow-sm'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
              }
            `}
            title={t('comments.addEmoji') || 'Add emoji'}
          >
            <Smile className="w-4 h-4" />
          </button>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div
              ref={emojiPickerRef}
              className="absolute right-0 bottom-full mb-2 bg-white border-2 border-gray-200 rounded-xl shadow-lg p-3 z-50 animate-in fade-in slide-in-from-bottom-2"
              style={{ width: '280px' }}
            >
              <div className="grid grid-cols-4 gap-2">
                {PROFESSIONAL_EMOJIS.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleEmojiClick(item.emoji)}
                    className="
                      w-10 h-10 flex items-center justify-center text-2xl
                      rounded-lg hover:bg-gray-100 transition-colors
                      focus:outline-none focus:ring-2 focus:ring-blue-500
                    "
                    title={item.label}
                    disabled={isSubmitting}
                  >
                    {item.emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Character Counter */}
        <div className="flex justify-end">
          <div
            className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
              isAtLimit
                ? 'bg-red-100 text-red-700'
                : isNearLimit
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {charactersRemaining}/{MAX_CHARACTERS}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-3">
          {/* Internal Toggle */}
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
              disabled={isSubmitting}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="flex items-center gap-1.5 text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
              <Lock className="w-4 h-4" />
              <span>{t('comments.markAsInternal')}</span>
            </div>
          </label>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!comment.trim() || isAtLimit || isSubmitting}
            className={`
              flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm
              transition-all duration-200 transform
              ${
                !comment.trim() || isAtLimit || isSubmitting
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg hover:scale-105 active:scale-95'
              }
            `}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('comments.submitting')}</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>{t('comments.submit')}</span>
              </>
            )}
          </button>
        </div>

        {/* Helper Text */}
        <p className="text-xs text-gray-500">
          {t('comments.helperText')}
        </p>
      </form>
    </div>
  );
};

export default CommentInput;

